<template>
  <Layout
    class="onboarding-loading"
    :title="currentTitle"
    :titleTransition="true"
  >
    <template #aboveTitle>
      <Rive
        class="onboarding-loading__rive"
        :src="getImage(page.animation.src)"
        :width="page.animation.width"
        :height="page.animation.height"
        :animations="currentAnimation"
        fit="FitWidth"
        alignment="BottomCenter"
      />
    </template>
  </Layout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { getImage, QuestionnaireView } from '@/models/onboarding'
import type { LoadingPage } from '@/models/onboarding.types'

import Rive from '@/app/components/Rive.vue'
import Layout from '../Layout.vue'
import { waitTimer } from '@/helpers/timer'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as LoadingPage
})

const isLoading = ref(true)

const currentAnimation = computed(() => {
  return isLoading.value ? 'loading' : 'loaded'
})

const currentTitle = computed(() => {
  return isLoading.value ? page.value.title : page.value.titleDone
})

onMounted(async () => {
  // This component was previously used as a loading screen while the First
  // Reading List was being created, so there is a 3-second wait here.
  // But the first reading lists are now created later on; see #18992.
  // So this 3-second wait is probably no longer needed (and the component
  // is no longer used anyway), but we keep it here in case future
  // onboarding work expects it.
  await waitTimer(3000)
  isLoading.value = false
  props.view.showContinueButton()
})
</script>

<style lang="scss" scoped>
.onboarding-loading {
  gap: 24px;
  padding-top: 0;
  // Extra space at the bottom to accomodate the footer
  // which is absolute positioned.
  padding-bottom: calc(var(--onboarding-padding) * 4);
  @media (min-height: 650px) {
    justify-content: center;
  }

  &:deep(.onboarding-layout__above-title) {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &:deep(.onboarding-layout__text) {
    // Prevent layout shifts when the text updates.
    min-height: 80px;
  }

  &:deep(.onboarding-layout__above-title) {
    min-height: 0;
  }

  &__rive {
    max-width: 285px;
  }

  &:deep(~ .onboarding-questionnaire__button-wrapper) {
    position: absolute;
    bottom: 0;
  }
}
</style>
